// Shared helpers for the Form Wizard staff screens (no React here).

export const CATEGORY_ORDER = ["screening", "questionnaire", "intake", "history", "consent", "policy", "abn", "survey", "other"];
export const CATEGORY_LABEL = {
  screening: "Screenings",
  questionnaire: "Questionnaires",
  intake: "Intake",
  history: "History",
  consent: "Consents",
  policy: "Policies",
  abn: "ABN",
  survey: "Surveys",
  other: "Other",
};

export const CHANNELS = ["sms", "email", "link", "kiosk"];
export const CHANNEL_LABEL = {
  sms: "Text message",
  email: "Email",
  kiosk: "Tablet (hand this device to the patient now)",
  link: "Copy link",
};
export const CHANNEL_SHORT = { sms: "Text", email: "Email", kiosk: "Tablet", link: "Link" };

// Request status -> [label, tone]. Tones map to .fws-tone-* classes.
export const REQUEST_STATUS = {
  pending: ["Not sent yet", "muted"],
  sent: ["Sent", "info"],
  send_failed: ["Send failed", "bad"],
  blocked: ["Not sent (texting off)", "warn"],
  opened: ["Opened", "info"],
  in_progress: ["In progress", "info"],
  completed: ["Completed", "ok"],
  expired: ["Expired", "muted"],
  cancelled: ["Cancelled", "muted"],
  locked: ["Locked (DOB tries)", "bad"],
};
export const SUBMISSION_STATUS = {
  pending: ["Not started", "muted"],
  in_progress: ["In progress", "info"],
  submitted: ["Submitted", "ok"],
  void: ["Void", "muted"],
};
export const EMR_STATUS = {
  none: ["Not in EMR", "muted"],
  queued: ["EMR queued", "info"],
  posting: ["Posting to EMR", "info"],
  posted: ["In EMR", "ok"],
  failed: ["EMR failed", "bad"],
  skipped: ["EMR skipped", "warn"],
  // the post went out and Athena never answered: it may be in the chart (2026-10-06)
  unknown: ["EMR unconfirmed - check chart", "warn"],
};

// The EMR status to show: a post that may be in the chart is "unconfirmed" whether the
// gateway recorded 'unknown' or the post was abandoned mid-way and is still 'posting'
// (it sets emr_uncertain for both - review 10-06).
export const emrStatusOf = (sub) => (sub && sub.emr_uncertain ? "unknown" : sub && sub.emr_status);

// Statuses in which the patient can still open the link.
export const OPEN_REQUEST_STATUSES = ["pending", "sent", "send_failed", "blocked", "opened", "in_progress"];
export const canCancelRequest = (r) => !!r && [...OPEN_REQUEST_STATUSES, "locked"].includes(r.status);
// Resend issues a new link (old one dies), unlocks and extends the expiry.
export const canResendRequest = (r) =>
  !!r && r.channel !== "kiosk" && [...OPEN_REQUEST_STATUSES, "locked", "expired"].includes(r.status);
export const canOpenOnTablet = (r) => !!r && r.channel === "kiosk" && OPEN_REQUEST_STATUSES.includes(r.status);

// Append ids not already present; the order of `list` is kept.
export function mergeForms(list, add) {
  const out = [...list];
  (add || []).forEach((id) => {
    if (!out.includes(id)) out.push(id);
  });
  return out;
}

export function moveItem(list, index, delta) {
  const to = index + delta;
  if (index < 0 || index >= list.length || to < 0 || to >= list.length) return list;
  const out = [...list];
  const [it] = out.splice(index, 1);
  out.splice(to, 0, it);
  return out;
}

export const statusInfo = (map, key) => map[key] || [key ? String(key).replace(/_/g, " ") : "—", "muted"];

// "{en, es}" or a plain string.
export function titleOf(x, lang = "en") {
  if (!x) return "";
  if (typeof x === "string") return x;
  return x[lang] || x.en || x.es || "";
}

// Leading YYYY-M-D of a date / datetime string -> MM/DD/YYYY ("" when none).
export function fmtDate(v) {
  if (!v) return "";
  const m = String(v).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return String(v);
  return `${m[2].padStart(2, "0")}/${m[3].padStart(2, "0")}/${m[1]}`;
}

// Server timestamps are UTC ISO-8601 ("...Z"); shown in the browser's zone.
export function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export function fmtShortDate(d) {
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
}

export function maskPhone(p) {
  const d = String(p || "").replace(/\D/g, "");
  return d.length >= 4 ? `***-***-${d.slice(-4)}` : "";
}

export function maskEmail(e) {
  const m = String(e || "").match(/^([^@\s])[^@\s]*@([^@.\s])[^@\s]*?(\.[^@\s]+)$/);
  return m ? `${m[1]}***@${m[2]}***${m[3]}` : "";
}

// dssearch rows -> one entry per patient (it returns one row per appointment).
export function patientsFromSearch(rows) {
  const seen = new Set();
  const out = [];
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (!r || !r.patientid || seen.has(r.patientid)) return;
    seen.add(r.patientid);
    const full = String(r.patientinfo || "").trim() || [r.firstname, r.lastname].filter(Boolean).join(" ");
    out.push({ patientid: r.patientid, name: full, dob: r.dob || "", phone: r.mobilephone || r.phone || "", email: r.email || "" });
  });
  return out;
}

export function patientDisplayName(p) {
  if (!p) return "";
  if (p.name) return p.name;
  const full = [p.firstname || p.firstName, p.lastname || p.lastName].filter(Boolean).join(" ").trim();
  return full || p.fullName || p.patientname || "";
}

// Severity (bands: none|low|moderate|high|critical; flags: info|warning|critical)
// -> chip tone.
export function sevTone(sev) {
  if (sev === "critical" || sev === "high") return "bad";
  if (sev === "moderate" || sev === "warning") return "warn";
  if (sev === "low" || sev === "info") return "info";
  return "ok";
}

const SEV_RANK = { none: 0, low: 1, info: 1, moderate: 2, warning: 2, high: 3, critical: 4 };
export const sevRank = (s) => SEV_RANK[s] || 0;

// Severity of the primary band for a score, from the definition (the inbox
// list carries the score and band label only). Matches the label first, then
// the score range; null when the form has no bands.
export function bandSeverity(def, score, bandLabel) {
  const scoring = def && def.scoring;
  if (!scoring || !Array.isArray(scoring.scales)) return null;
  const scale = scoring.scales.find((s) => s.id === scoring.primary);
  if (!scale || !Array.isArray(scale.bands)) return null;
  if (bandLabel) {
    const byLabel = scale.bands.find((b) => titleOf(b.label, "en") === bandLabel);
    if (byLabel) return byLabel.severity || "none";
  }
  const n = Number(score);
  if (score === null || score === undefined || score === "" || Number.isNaN(n)) return null;
  const hit = scale.bands.find((b) => n >= Number(b.min) && n <= Number(b.max));
  return hit ? hit.severity || "none" : null;
}

export function fmtScore(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return String(Math.round(n * 100) / 100);
}

// Axios error -> {status, code, message, data}. Tolerates the formwiz
// envelope ({status, message, data: {code, ...}}), a top-level code, and
// FastAPI's {detail: ...}.
export function errInfo(err) {
  const r = err && err.response;
  const status = r ? Number(r.status) || 0 : 0;
  let body = r ? r.data : null;
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch (_) {
      body = { message: body };
    }
  }
  if (!body || typeof body !== "object" || (typeof Blob !== "undefined" && body instanceof Blob)) body = {};
  const detail = body.detail && typeof body.detail === "object" && !Array.isArray(body.detail) ? body.detail : {};
  const data = body.data && typeof body.data === "object" && !Array.isArray(body.data) ? body.data : {};
  const msg = body.message || detail.message || (typeof body.detail === "string" ? body.detail : "");
  let code = body.code || data.code || detail.code || body.error || "";
  if (!code && typeof msg === "string" && /^[a-z][a-z_]+$/.test(msg)) code = msg;
  return {
    status,
    code: String(code || ""),
    message: typeof msg === "string" ? msg : "",
    data: { ...detail, ...data, ...(body.request_id !== undefined ? { request_id: body.request_id } : {}) },
    network: !r,
  };
}

export const isUnavailable = (err) => {
  const { status } = errInfo(err);
  return status === 404;
};

// Copy text: async clipboard API, else a hidden textarea + execCommand.
export async function copyText(text) {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    /* fall through to the textarea path (insecure context, permission) */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand && document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch (_) {
    return false;
  }
}

// Practice display name rules (server validates too): <=60 chars, letters,
// digits, space and & ' . , ( ) -, no "http"/"www".
export function displayNameError(v) {
  const s = String(v || "");
  if (!s) return "";
  if (s.length > 60) return "60 characters at most";
  if (!/^[A-Za-z0-9 &'.,()-]+$/.test(s)) return "Letters, numbers, spaces and & ' . , ( ) - only";
  if (/http|www/i.test(s)) return "No web addresses";
  return "";
}

// Public link base: https:// on one of the server's allowed hosts; a path
// prefix (devcare /branch/<name>) is allowed.
export function baseUrlError(v, hosts) {
  const s = String(v || "").trim();
  if (!s) return "";
  let u;
  try {
    u = new URL(s);
  } catch (_) {
    return "Not a valid URL";
  }
  if (u.protocol !== "https:") return "Must start with https://";
  if (u.search || u.hash) return "No ? or # parts";
  if (Array.isArray(hosts) && hosts.length && !hosts.includes(u.hostname)) return `Host must be one of: ${hosts.join(", ")}`;
  return "";
}
